/**
 * Re-quote shortcut workflow runner — TS-060 (DEC-COUNCIL-015).
 *
 * Encapsulates the Stage 1.5 -> Stage 4 re-quote fast-track extraction,
 * document discovery, pricing staleness assessment, and state application.
 */

import React from 'react';
import {
    extractRequoteScope,
    findPreviousQuoteDoc,
    partitionByRequoteOrigin,
    assessRequoteStaleness,
} from './requote-scope-extractor';
import {
    captureProjectRunContext,
    isProjectRunContextCurrent,
    type ProjectRunContext,
} from '../utils/project-run-context';
import { isBrainProject, quoteBrainRefusalMessage } from '../utils/quote-brain-project';

export interface RequoteScopeExtractionResult {
    ok: boolean;
    items: any[];
    unresolvedChangeInstructions: string[];
    stalePricingWarning: boolean;
    stalePricingReason: string;
    extractionNotes: string;
    modelUsed: string;
    previousQuoteDocName: string | null;
    previousQuoteDate: string | null;
    generatedAt: string;
}

/** Live project identity read at call time (never from a render closure). */
export interface RequoteLiveRunIdentity {
    projectId: string | null;
    loadGeneration: number;
    signal: AbortSignal | null;
}

export interface ExecuteRequoteShortcutOptions {
    isRequoteShortcutRunning: boolean;
    apiKey: string | null;
    model?: string;
    projectSummary: any;
    documents: any[];
    formData: any;
    currentProjectId: string | null;
    setToast: (toast: any) => void;
    setIsRequoteShortcutRunning: (running: boolean) => void;
    setScope: React.Dispatch<React.SetStateAction<any[]>>;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
    setCompletedStages: React.Dispatch<React.SetStateAction<Set<number>>>;
    setStageWithTransition: (stage: number) => void;
    /**
     * Reads the project that is on screen RIGHT NOW. The run captures it once
     * before any await and checks it again after each one: only the project
     * (and load session) that started the extraction may receive its result.
     * Optional so headless callers keep the old behaviour; App always supplies it.
     */
    getLiveRunIdentity?: () => RequoteLiveRunIdentity;
    /**
     * Holds the token of the LATEST run started. Never cleared, so a run can
     * tell "I am still the latest" from "a newer run started" even after that
     * newer run has finished. Busy cleanup AND feedback both check it: a
     * superseded run releases no lock and emits no notice.
     */
    runOwnerRef?: { current: unknown };
    /**
     * Returns a value that changes whenever the operator navigates (stage,
     * page, or the Project Summary sub-view), including away-and-back. Read at
     * the start of the run and again at settlement. A changed value means the
     * operator chose another view while the AI call ran: the extracted data is
     * still valid and is kept, but the run must not pull them back to Stage 4.
     */
    getNavigationIntent?: () => unknown;
    /**
     * Returns the live scope array. Read at the start of the run and again at
     * settlement, whether or not the operator navigated: if the live array is
     * no longer the one captured at start (scope was authored or published
     * meanwhile, for example an Assistant proposal applied on the Summary), the
     * extraction is not applied over it. Independent of navigation intent.
     */
    getLiveScope?: () => unknown;
    /**
     * The previous quote could not be read (no usable items, or the extractor failed). Not called for a cancel, a project
     * change or a superseded run. App falls back to the normal route by itself under Full AI (SPEC 2026-10-21 C13).
     */
    onExtractionFailed?: () => void;
}

export async function executeRequoteShortcut({
    isRequoteShortcutRunning,
    apiKey,
    model,
    projectSummary,
    documents,
    formData,
    currentProjectId,
    setToast,
    setIsRequoteShortcutRunning,
    setScope,
    setFormData,
    setCompletedStages,
    setStageWithTransition,
    getLiveRunIdentity,
    runOwnerRef,
    getNavigationIntent,
    getLiveScope,
    onExtractionFailed,
}: ExecuteRequoteShortcutOptions): Promise<boolean> {
    if (isRequoteShortcutRunning) return false;
    // Quote Brain DESIGN.md 2.12: a brain project's items are the Quote
    // Brain's, so the shortcut never reads the previous quote into Stage 4
    // over them. Checked here, before the extraction is paid for, and again
    // before the write, because a brain run can go live while it reads.
    const brainProjectId = (): string | null => getLiveRunIdentity?.().projectId ?? currentProjectId ?? null;
    const refuseBrainProject = (): boolean => {
        const projectId = brainProjectId();
        if (!isBrainProject(projectId, { scope: getLiveScope ? getLiveScope() : undefined })) return false;
        setToast({ message: quoteBrainRefusalMessage('Skip to Scope Builder', projectId), type: 'info' });
        return true;
    };
    if (refuseBrainProject()) return false;
    if (!apiKey) {
        setToast({
            type: 'error',
            message: 'API key is required to run the re-quote shortcut. Add your key in Settings.',
        });
        return false;
    }
    const signal = projectSummary?.requote_signal;
    if (!signal?.is_requote || !signal?.previous_quote_doc_name) {
        setToast({
            type: 'error',
            message: 'No re-quote signal detected for this project.',
        });
        return false;
    }
    // CN5: a summary made after CN5 records the document the model pointed at
    // by its number, resolved to an id. Older summaries carry only a name.
    const idIndex = signal.previous_quote_doc_id && Array.isArray(documents)
        ? documents.findIndex((d: any) => d && String(d.id ?? '') === String(signal.previous_quote_doc_id))
        : -1;
    const found = idIndex >= 0
        ? { doc: documents[idIndex], index: idIndex }
        : findPreviousQuoteDoc(documents, signal.previous_quote_doc_name);
    if (!found) {
        setToast({
            type: 'error',
            message: `Couldn't find "${signal.previous_quote_doc_name}" in the project documents. Re-import and try again.`,
        });
        onExtractionFailed?.();
        return false;
    }
    // Ownership. Everything below the first await may only publish while the
    // project and load session that started this run are still live. A switch
    // or reopen replaces the project controller, so the captured signal is
    // also what stops the extractor spending tokens for a project that is gone.
    const runContext: ProjectRunContext | null = getLiveRunIdentity
        ? captureProjectRunContext(getLiveRunIdentity())
        : null;
    // Same project and load session, ignoring the abort signal. Tells a run
    // cancelled on its own project (STOP, notes edit) apart from a run for a
    // project the operator has left.
    const identityOnlyContext: ProjectRunContext | null = runContext
        ? captureProjectRunContext({ ...runContext, signal: null })
        : null;
    const token = {};
    if (runOwnerRef) runOwnerRef.current = token;
    // True while no newer run has started. The token is deliberately never
    // cleared: a retry that already finished must still mark this run as old.
    const isLatestRun = (): boolean => !runOwnerRef || runOwnerRef.current === token;
    const ownsProject = (): boolean => !runContext || !getLiveRunIdentity
        || isProjectRunContextCurrent(runContext, getLiveRunIdentity());
    const projectUnchanged = (): boolean => !identityOnlyContext || !getLiveRunIdentity
        || isProjectRunContextCurrent(identityOnlyContext, getLiveRunIdentity());
    // Publication authority: right project, right load session, signal not
    // cancelled, and no newer run started.
    const ownsRun = (): boolean => ownsProject() && isLatestRun();
    // Feedback authority is narrower than project identity. A cancelled run
    // that is still the latest may say "stopped, try again". A run a retry has
    // superseded owns no feedback at all, whether that retry is pending or
    // already done, or its notice would overwrite the retry's outcome.
    const ownsFeedback = (): boolean => projectUnchanged() && isLatestRun();
    // Navigation intent at the start. Compared at settlement.
    const intentAtStart = getNavigationIntent ? getNavigationIntent() : undefined;
    const navigationIntentUnchanged = (): boolean => !getNavigationIntent
        || getNavigationIntent() === intentAtStart;
    const scopeAtStart = getLiveScope ? getLiveScope() : undefined;

    // Busy state belongs to the run that set it. It is released at most once:
    // by the abort listener (so a dependency that ignores the signal cannot pin
    // the button) or by the finally. Never when a newer run owns the flag.
    let released = false;
    const releaseBusy = () => {
        if (released) return;
        released = true;
        if (!isLatestRun()) return;
        setIsRequoteShortcutRunning(false);
    };
    const abortSignal = runContext?.signal || null;
    abortSignal?.addEventListener('abort', releaseBusy, { once: true });

    setIsRequoteShortcutRunning(true);
    try {
        const result = (await extractRequoteScope({
            apiKey,
            model: model || '',
            previousQuoteDoc: found.doc,
            changeRequestSummary: signal.change_request_summary || '',
            additionalNotes: formData?.notes || '',
            projectId: runContext?.projectId ?? currentProjectId ?? undefined,
            ...(abortSignal ? { signal: abortSignal } : {}),
        })) as unknown as RequoteScopeExtractionResult;
        if (!ownsRun()) {
            // Cancelled, left behind by a project change, or superseded by a
            // retry. Nothing may reach the project now on screen. Only the
            // latest run, on its own project, is owed a "try again" notice.
            if (ownsFeedback()) notifyStopped(setToast);
            return false;
        }
        if (refuseBrainProject()) return false;
        if (!result || !result.ok || !Array.isArray(result.items) || result.items.length === 0) {
            setToast({
                type: 'warning',
                message: `Couldn't extract scope from "${found.doc.name}". Falling back to the normal Risk Analysis path.`,
            });
            onExtractionFailed?.();
            return false;
        }
        const { active, removedHistory } = partitionByRequoteOrigin(result.items);
        const staleness = assessRequoteStaleness({
            previousQuoteDate: result.previousQuoteDate,
            activeItems: active,
            removedHistory,
            aiWarning: !!result.stalePricingWarning,
        });
        const stamped = active.map(it => ({
            ...it,
            editedBy: 'ai-requote-shortcut',
            editedAt: new Date().toISOString(),
        }));
        // Three separate contracts, each decided on its own evidence:
        //   - project ownership (above): the result belongs to this project;
        //   - scope freshness (here): the scope the extraction was built
        //     against is still the live scope. This does NOT depend on the
        //     operator having navigated: scope also changes while they sit on
        //     the Summary (an Assistant proposal applied there, or a Scope
        //     Refresh that publishes after the shortcut started);
        //   - navigation intent (below): if the operator chose another view
        //     while the AI call ran (Back, a pill, another page, or away and
        //     back) they keep that view.
        if (getLiveScope && getLiveScope() !== scopeAtStart) {
            // Scope was authored or published in the meantime. Applying the
            // extraction would overwrite it, so nothing is applied and the
            // operator stays where they are.
            setToast({
                type: 'info',
                message: 'Re-quote extraction finished, but your scope changed while it ran, so it was not applied. Press Skip to Scope Builder again in the Summary tab to load it.',
            });
            return false;
        }
        const navigates = navigationIntentUnchanged();
        setScope(stamped);
        setFormData((prev: any) => ({
            ...prev,
            requoteContext: {
                isRequote: true,
                previousQuoteDocName: result.previousQuoteDocName,
                extractionNotes: result.extractionNotes || '',
                unresolvedChangeInstructions: result.unresolvedChangeInstructions || [],
                stalePricingWarning: staleness.stalePricingWarning,
                stalePricingReason: staleness.stalePricingReason,
                previousQuoteDate: result.previousQuoteDate || null,
                removedHistory,
                changeRequestSummary: signal.change_request_summary || '',
                extractedAt: result.generatedAt,
                modelUsed: result.modelUsed,
            },
        }));
        // 5B / O-F11: the shortcut goes from the Summary to Scope Builder and SKIPS Risk and Method, so it records Stage 1 only, the way the
        // Fast-Track Summary Next does. It used to record 1, 2 and 3, which showed Risk and Method complete for work that never ran. The
        // project's workflow mode is left as it was (a Full-AI project stays Full AI), and `requoteContext.isRequote` (above) is the
        // provenance that keeps Scope Builder reachable and the completion record honest.
        setCompletedStages((prev: Set<number>) => new Set([...prev, 1]));
        const itemsSummary = `${stamped.length} items${removedHistory.length ? `, ${removedHistory.length} removed` : ''}`;
        if (navigates) {
            setStageWithTransition(4);
            setToast({
                type: 'success',
                message: `Re-quote scope loaded (${itemsSummary}). Review + edit in Scope Builder.`,
            });
        } else {
            setToast({
                type: 'success',
                message: `Re-quote scope loaded (${itemsSummary}). It is ready in Scope Builder whenever you want to review it.`,
            });
        }
        return true;
    } catch (err: any) {
        if (!ownsRun() || err?.name === 'AbortError') {
            // A project change, a cancellation or a newer run is not an
            // extraction failure: no error toast, and nothing published to
            // whatever is on screen. Only the latest run on its own project
            // is owed the "try again" notice.
            if (ownsFeedback()) notifyStopped(setToast);
            return false;
        }
        console.error('[QM] handleRequoteShortcut failed:', err);
        setToast({
            type: 'error',
            message: `Re-quote extraction failed: ${err?.message || 'Unknown error'}. Falling back to the normal Risk Analysis path.`,
        });
        onExtractionFailed?.();
        return false;
    } finally {
        abortSignal?.removeEventListener('abort', releaseBusy);
        releaseBusy();
    }
}

function notifyStopped(setToast: (toast: any) => void): void {
    setToast({
        type: 'info',
        message: 'Re-quote extraction was stopped. Click Skip to Scope Builder to try again, or NEXT to run Risk Analysis as usual.',
    });
}
